import type { VoidFunctionComponent } from 'react';
import { ControlLabel, FormGroup } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import { CANNOT_DISABLE_NON_CUSTOM_TEAM_ROLES_ERROR } from '../../../../../shared/organizationPackages/errors';
import type { GeneralSettings } from '../../../../../shared/settings/general';
import { orgRolesList } from '../../../../helpers/role';
import { useDispatchOnMount } from '../../../../hooks';
import RolesModules from '../../../../redux/roles';
import { AccessGate, ErrorAlert, Select } from '../../../generic';
import Flex from '../../../generic/Flex';
import { icons } from '../../../generic/Icon';
import Text from '../../../generic/Text';
import Toggle from '../../../generic/Toggle';
import TooltipIcon from '../../../generic/TooltipIcon';

type Props = {
  settings: GeneralSettings;
  onUpdateSettings: (settings: Partial<GeneralSettings>) => void;
  isDisabled: boolean;
  errors: string[] | null;
};

/**
 * Allow users to set the default role for new users.
 * This is only available to premium organizations.
 */
const UsersSection: VoidFunctionComponent<Props> = ({ settings, isDisabled, onUpdateSettings, errors }) => {
  const roles = useSelector(RolesModules.selectors.getRoles);

  useDispatchOnMount(RolesModules.actions.fetchAll);

  const selectedDefaultRole = settings.defaultRoleId
    ? { label: roles[settings.defaultRoleId]?.name, value: roles[settings.defaultRoleId]?.id }
    : { label: 'None', value: null };

  return (
    <AccessGate level="premium" behavior="hide">
      {errors?.some((error) => error.includes(CANNOT_DISABLE_NON_CUSTOM_TEAM_ROLES_ERROR)) && (
        <ErrorAlert status={{ errors }} />
      )}
      <Text.H3>Users</Text.H3>
      <div>
        <FormGroup className="default-role">
          <Flex direction="column" align="stretch">
            <ControlLabel>Default Role for New Users</ControlLabel>
            <Select
              id="org-default-role"
              isCreatable={false}
              isDisabled={isDisabled}
              value={selectedDefaultRole}
              options={orgRolesList(roles).map((role) => ({ label: role.name, value: role.id }))}
              onChange={(role) => {
                if (!role) {
                  return;
                }
                onUpdateSettings({
                  defaultRoleId: role.value,
                });
              }}
            />
          </Flex>
        </FormGroup>
        <FormGroup>
          <Flex justify="flex-start" gap="8px">
            <Toggle
              checked={!settings.disableNonCustomTeamUserRoles}
              isDisabled={isDisabled}
              onChange={(isChecked) => {
                onUpdateSettings({
                  disableNonCustomTeamUserRoles: !isChecked,
                });
              }}
            />
            <ControlLabel>
              Enable FOSSA default team roles{' '}
              <TooltipIcon
                id="disable-non-custom-team-user-roles-tooltip"
                tooltip="Disabling this will hide the built in roles that FOSSA offers out of the box."
                icon={icons.infoCircle}
              />
            </ControlLabel>
          </Flex>
        </FormGroup>
      </div>
    </AccessGate>
  );
};

export default UsersSection;
